import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, ArrowRight } from 'lucide-react';

const pricingItems = [
  {
    service: 'Servicios de Filmación',
    price: '$59 USD',
    prefix: 'Desde',
    description: 'Filmación profesional de video para marcas',
    features: ['Alta calidad', 'Múltiples ángulos', 'En locación'],
  },
  {
    service: 'Edición de Video',
    price: '$49 USD',
    prefix: 'Desde',
    description: 'Post-producción y edición experta',
    features: ['Color grading', 'Motion graphics', 'Diseño de sonido'],
  },
  {
    service: 'Paquete Filmación + Edición',
    price: '$115 USD',
    prefix: 'Desde',
    description: 'Solución completa de producción de video',
    features: ['Producción completa', 'Revisiones incluidas', 'Entrega rápida'],
    featured: true,
  },
  {
    service: 'Gestión de Redes Sociales',
    price: '$139 USD',
    prefix: 'Desde',
    suffix: '/ mes',
    description: 'Estrategia y ejecución completa de redes',
    features: ['Creación de contenido', 'Programación', 'Reportes de analytics'],
  },
  {
    service: 'Logo & Identidad de Marca',
    price: '$189 USD',
    prefix: 'Desde',
    description: 'Diseño completo de identidad de marca',
    features: ['Variaciones de logo', 'Manual de marca', 'Archivos fuente'],
  },
  {
    service: 'Desarrollo Web',
    price: '$299 – $2500 USD',
    prefix: '',
    description: 'Diseño y desarrollo web personalizado',
    features: ['Diseño responsive', 'SEO optimizado', 'Integración CMS'],
    featured: true,
  },
  {
    service: 'Desarrollo de Apps',
    price: '$299 – $1500 USD',
    prefix: '',
    description: 'Desarrollo de aplicaciones móviles y web',
    features: ['Multiplataforma', 'Integración API', 'Testing de usuario'],
  },
  {
    service: 'Sistemas de IA',
    price: '$199 – $1599 USD',
    prefix: '',
    description: 'Soluciones inteligentes de automatización',
    features: ['Chatbots custom', 'Automatización', 'Integración IA'],
  },
];

export default function Pricing() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="pricing"
      ref={ref}
      className="relative py-24 md:py-32 bg-black"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#D4AF37] mb-4 block">
            Investment
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Inversión en Servicios
            </h2>
            <p className="text-sm text-zinc-500 max-w-md">
              El precio final depende del alcance y requerimientos del proyecto.
              Contáctanos para una cotización detallada.
            </p>
          </div>
        </motion.div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pricingItems.map((item, index) => (
            <motion.div
              key={item.service}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
              className={`group relative p-6 md:p-8 border transition-all duration-500 ${
                item.featured
                  ? 'bg-[#D4AF37]/5 border-[#D4AF37]/30 hover:border-[#D4AF37]'
                  : 'bg-zinc-950/50 border-white/5 hover:border-[#D4AF37]/50'
              }`}
              data-testid={`pricing-${index}`}
            >
              {item.featured && (
                <div className="absolute top-4 right-4">
                  <span className="text-[10px] tracking-[0.15em] uppercase px-2 py-1 bg-[#D4AF37] text-black font-bold">
                    Popular
                  </span>
                </div>
              )}

              {/* Service Name */}
              <h3 className="text-sm font-medium tracking-wide text-zinc-400 mb-4 pr-16">
                {item.service}
              </h3>

              {/* Price */}
              <div className="mb-4">
                {item.prefix && (
                  <span className="text-xs text-zinc-600 block mb-1">{item.prefix}</span>
                )}
                <span className="text-2xl md:text-3xl font-bold text-white group-hover:text-[#D4AF37] transition-colors">
                  {item.price}
                </span>
                {item.suffix && (
                  <span className="text-sm text-zinc-500 ml-1">{item.suffix}</span>
                )}
              </div>

              {/* Description */}
              <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
                {item.description}
              </p>

              {/* Features */}
              <ul className="space-y-2">
                {item.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-xs text-zinc-500">
                    <Check size={12} className="text-[#D4AF37] flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <motion.button
            onClick={scrollToContact}
            className="group inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-10 py-5 text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="pricing-get-quote-btn"
          >
            Solicitar Cotización
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
